
import { useState } from 'react';
import { Check, X, AlertTriangle, FileText, Download } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { PendingAccount, approveAccount, rejectAccount } from '@/utils/adminUtils';
import { parseQualificationDocuments, downloadDocument, getFileTypeIcon, formatFileSize } from '@/utils/documentUtils';

interface PendingAccountsTabProps {
  accounts: PendingAccount[];
  onAccountApproved: () => void;
  onAccountRejected: () => void;
}

export function PendingAccountsTab({ accounts, onAccountApproved, onAccountRejected }: PendingAccountsTabProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
  const [confirmReject, setConfirmReject] = useState<PendingAccount | null>(null);
  const isMobile = useIsMobile();
  
  const getInitials = (firstName: string = '', lastName: string = '') => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleApprove = async (account: PendingAccount) => {
    if (!account || !account.id) {
      toast({
        title: "Error",
        description: "Invalid account data",
        variant: "destructive",
      });
      return;
    }
    
    // Convert id to string if it's a number
    const accountId = String(account.id);
    setIsLoading(prev => ({ ...prev, [accountId]: true }));
    
    try {
      const success = await approveAccount(accountId);
      
      if (success) {
        toast({
          title: "Account Approved",
          description: `${account.details?.firstName || ''} ${account.details?.lastName || ''}'s account has been approved.`,
        });
        if (onAccountApproved) onAccountApproved();
      } else {
        toast({
          title: "Error",
          description: "Failed to approve account",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error approving account:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while approving the account",
        variant: "destructive",
      });
    } finally {
      setIsLoading(prev => ({ ...prev, [accountId]: false }));
    }
  };

  const handleReject = async (account: PendingAccount) => {
    if (!account || !account.id) {
      toast({
        title: "Error",
        description: "Invalid account data",
        variant: "destructive",
      });
      return;
    }
    
    // Convert id to string if it's a number
    const accountId = String(account.id);
    setIsLoading(prev => ({ ...prev, [accountId]: true }));
    setConfirmReject(null);
    
    try {
      const success = await rejectAccount(accountId);
      
      if (success) {
        toast({
          title: "Account Rejected",
          description: `${account.details?.firstName || ''} ${account.details?.lastName || ''}'s account has been rejected.`,
        });
        if (onAccountRejected) onAccountRejected();
      } else {
        toast({
          title: "Error",
          description: "Failed to reject account",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error rejecting account:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while rejecting the account",
        variant: "destructive",
      });
    } finally {
      setIsLoading(prev => ({ ...prev, [accountId]: false }));
    }
  };

  if (!accounts || accounts.length === 0) {
    return (
      <div className="bg-white rounded-lg p-4 md:p-8 text-center">
        <div className="mx-auto flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-blue-100 mb-3 md:mb-4">
          <Check className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
        </div>
        <h3 className="text-base md:text-lg font-medium text-gray-900">No pending accounts</h3>
        <p className="mt-1 md:mt-2 text-xs md:text-sm text-gray-500">
          There are no pending accounts that require approval at this time.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg md:text-xl font-bold tracking-tight">Pending Accounts</h2>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">
            Review and approve or reject pending user accounts.
          </p>
        </div>
        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 flex-shrink-0 text-xs">
          {accounts.length} Pending
        </Badge>
      </div>

      <Separator />

      <div className={`grid gap-4 md:gap-6 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
        {accounts.map((account) => {
          // Extract first and last name from name string, or from details if available
          const firstName = account.details?.firstName || account.name?.split(' ')[0] || '';
          const lastName = account.details?.lastName || account.name?.split(' ')[1] || '';
          
          return (
          <Card key={account.id} className="overflow-hidden">
            <CardHeader className="bg-muted/50 pb-3 md:pb-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2 md:space-x-4 flex-1 min-w-0">
                  <Avatar className="h-8 w-8 md:h-10 md:w-10 border flex-shrink-0">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {getInitials(firstName, lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-sm md:text-base truncate">
                      {firstName} {lastName}
                    </CardTitle>
                    <CardDescription className="text-xs truncate">
                      {account.email}
                    </CardDescription>
                  </div>
                </div>
                <Badge 
                  variant={account.profileType === 'mentor' ? 'default' : 'outline'}
                  className="text-xs flex-shrink-0"
                >
                  {account.profileType}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-3 md:p-4">
              <div className="grid gap-2 text-xs md:text-sm">
                <div className={`grid ${isMobile ? 'grid-cols-1 gap-1' : 'grid-cols-3 gap-1'}`}>
                  <div className="font-medium">Registered:</div>
                  <div className={`text-gray-500 ${isMobile ? '' : 'col-span-2'}`}>
                    {new Date(account.appliedAt).toLocaleDateString()}
                  </div>
                </div>
{(() => {
                  const documents = parseQualificationDocuments(account.qualification_document_url);
                  if (documents.length === 0) return null;
                  
                  return (
                    <div className={`grid ${isMobile ? 'grid-cols-1 gap-1' : 'grid-cols-3 gap-1'}`}>
                      <div className="font-medium">Documents:</div>
                      <div className={isMobile ? '' : 'col-span-2'}>
                        <div className="space-y-1">
                          {documents.map((doc, index) => (
                            <Button
                              key={index}
                              variant="ghost"
                              size="sm"
                              className="h-auto p-1 text-blue-600 hover:text-blue-800 w-full text-left justify-start"
                              onClick={async () => {
                                try {
                                  await downloadDocument(doc);
                                  toast({
                                    title: "Download Started",
                                    description: `Downloading ${doc.filename}`,
                                  });
                                } catch (error) {
                                  toast({
                                    title: "Download Failed",
                                    description: "Unable to download the document. Please try again.",
                                    variant: "destructive",
                                  });
                                }
                              }}
                            >
                              <span className="mr-1">{getFileTypeIcon(doc.file_type)}</span>
                              <div className="flex flex-col items-start flex-1 min-w-0">
                                <span className="text-xs font-medium truncate max-w-full">
                                  {doc.filename}
                                </span>
                                {doc.file_size && (
                                  <span className="text-xs text-muted-foreground">
                                    {formatFileSize(doc.file_size)}
                                  </span>
                                )}
                              </div>
                              <Download className="h-3 w-3 ml-1 flex-shrink-0" />
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </CardContent>
            <CardFooter className="flex justify-between border-t p-3 md:p-4 bg-muted/20 gap-2">
              <Button 
                variant="outline"
                size={isMobile ? "sm" : "sm"}
                className="text-destructive border-destructive hover:bg-destructive/10 flex-1 md:flex-initial"
                onClick={() => setConfirmReject(account)}
                disabled={isLoading[String(account.id)]}
              >
                <X className="h-3 w-3 md:h-4 md:w-4 mr-1" /> 
                {isMobile ? 'Reject' : 'Reject'}
              </Button>
              <Button 
                size={isMobile ? "sm" : "sm"}
                className="text-white bg-green-600 hover:bg-green-700 flex-1 md:flex-initial"
                onClick={() => handleApprove(account)}
                disabled={isLoading[String(account.id)]}
              >
                {isLoading[String(account.id)] ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isMobile ? 'Processing...' : 'Processing...'}
                  </span>
                ) : (
                  <>
                    <Check className="h-3 w-3 md:h-4 md:w-4 mr-1" /> 
                    {isMobile ? 'Approve' : 'Approve'}
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )})}
      </div>

      {/* Confirmation Dialog for Rejection */}
      <AlertDialog open={!!confirmReject} onOpenChange={(open) => !open && setConfirmReject(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
              Confirm Rejection
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reject {confirmReject?.details?.firstName || confirmReject?.name?.split(' ')[0] || ''} {confirmReject?.details?.lastName || confirmReject?.name?.split(' ')[1] || ''}'s account? 
              This action cannot be undone, and the user will need to register again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" 
              onClick={() => confirmReject && handleReject(confirmReject)}
            >
              Reject Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
